/**
 * SunoPersonaPicker — pill в виджете для выбора персоны-голоса.
 *
 * По клику открывает sheet со списком персон юзера. Выбранная персона
 * сохраняется в `settings.audioPersonaId` и отправляется в generate.
 * «Не использовать» — вариант без персоны (дефолт).
 *
 * Для extend-операций: если передан `sourceTrackId`, персоны созданные
 * из этого трека поднимаются наверх и помечаются бейджем «из этого трека».
 */
import { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { personaBlockedReason } from '../utils/musicModelCaps'
import Sheet from '../../../components/Sheet'
import ToolsSubmenu from './ToolsSubmenu'
import { BbarSectionMode } from './bbarSectionContext'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { useSunoPersonas } from '../../../context/SunoPersonasContext'

interface Props {
	currentPersonaId: string | null | undefined
	onApply: (personaId: string | null) => void
	disabled?: boolean
	/** Пригашенный, но кликабельный вид (инструментал: голос неактуален,
	 *  выбор персоны выключит инструментал). */
	dimmed?: boolean
	/** ID source-трека для extend — персоны созданные из него подсвечиваются. */
	sourceTrackId?: string | null
	/** ID выбранной модели Suno — для хинта «лучше V5.5 для персон». */
	modelId?: string
}

export default function SunoPersonaPicker({ currentPersonaId, onApply, disabled, dimmed, sourceTrackId, modelId }: Props) {
	const [open, setOpen] = useState(false)
	const asSection = useContext(BbarSectionMode)
	const rowRef = useRef<HTMLButtonElement>(null)
	const { personas, loading } = useSunoPersonas()

	const selected = personas.find((p) => p.id === currentPersonaId) || null
	const label = selected ? (selected.name || 'Голос') : 'Голос'

	// Провайдер текущей генерации: Mureka-голос (vocal_id) нельзя
	// использовать в Suno-генерации и наоборот — фильтруем по совместимости.
	const isMureka = !!modelId && modelId.toLowerCase().startsWith('mureka')
	const currentProvider = isMureka ? 'mureka' : 'suno'
	// Версия без управляющих параметров голоса (Mureka o2: vocal_id «not
	// supported», 25 падений в реестре) — пилюля недоступна, персона снимается.
	const blockedReason = personaBlockedReason(modelId)
	const effDisabled = disabled || !!blockedReason
	const isSuno = !!modelId && modelId.startsWith('suno-')
	const isPersonaModel = modelId === 'suno-v5_5' || !!modelId?.startsWith('suno-v6')
	// Suno рекомендует V5.5 / V6 для персон — только для Suno (для Mureka нерелевантно).
	const showModelHint = isSuno && !isPersonaModel

	// Совместимые голоса + сортировка: для extend «свои из этого трека»
	// наверху, затем загруженные («свой голос»), затем остальные.
	const sortedPersonas = useMemo(() => {
		const compatible = personas.filter((p) => (p.provider || 'suno') === currentProvider)
		const rank = (p: typeof compatible[number]) => {
			if (sourceTrackId && p.source_track_id === sourceTrackId) return 0
			if (p.source === 'upload') return 1
			return 2
		}
		return [...compatible].sort((a, b) => rank(a) - rank(b))
	}, [personas, sourceTrackId, currentProvider])

	// Кросс-провайдер: персона привязана к провайдеру (Mureka-голос ≠ Suno-персона).
	// При смене модели на другой провайдер сбрасываем несовместимую персону —
	// иначе в генерацию уйдёт чужой persona_id и движок её отвергнет.
	useLayoutEffect(() => {
		if (!currentPersonaId) return
		if (blockedReason) { onApply(null); return }
		const cur = personas.find((p) => p.id === currentPersonaId)
		if (cur && (cur.provider || 'suno') !== currentProvider) onApply(null)
	}, [currentPersonaId, currentProvider, personas, onApply, blockedReason])

	// Общее тело списка голосов — рендерится и в Sheet (обычный режим), и в
	// drill-in сабменю (section-режим панели инструментов). Функционал 1:1.
	const body = (
		<div className="suno-persona-picker__body">
					<p className="suno-persona-picker__hint">
						{sourceTrackId
							? 'Чтобы продолжение звучало тем же голосом — выбери голос, созданный из этого трека (отмечен значком). Либо используй любой другой.'
							: isMureka
								? 'Голос — вокальный профиль для генерации. Запиши/загрузи свой на странице «Мои голоса» или создай из трека.'
								: 'Голоса — профили из твоих треков. Создаются в «Мои треки» → «Работать с треком» → «Персона».'}
					</p>

					{showModelHint && (
						<p className="suno-persona-picker__model-hint">
							⚡ Для лучшего результата с персонами Suno рекомендует модель <strong>V6</strong> или <strong>V5.5</strong> — переключи её в селекторе модели.
						</p>
					)}

					<button
						type="button"
						className={`suno-persona-picker__option${!currentPersonaId ? ' suno-persona-picker__option--selected' : ''}`}
						onClick={() => {
							onApply(null)
							setOpen(false)
						}}
					>
						<div className="suno-persona-picker__option-main">
							<div className="suno-persona-picker__option-title">Автоматический голос</div>
							<div className="suno-persona-picker__option-desc">
								Нейросеть выберет голос сама под стиль трека
							</div>
						</div>
						{!currentPersonaId && <span className="suno-persona-picker__check">✓</span>}
					</button>

					{loading && (
						<div className="suno-persona-picker__loading">Загружаем голоса...</div>
					)}

					{!loading && sortedPersonas.length === 0 && (
						<div className="suno-persona-picker__empty">
							{isMureka
								? 'Голосов пока нет. Создай свой на странице «Мои голоса» — запиши или загрузи аудио.'
								: 'У тебя пока нет голосов. Создай из любого трека в «Мои треки».'}
						</div>
					)}

					{!loading && sortedPersonas.length > 0 && sortedPersonas.map((p) => {
						const isOwn = !!sourceTrackId && p.source_track_id === sourceTrackId
						return (
							<button
								key={p.id}
								type="button"
								className={`suno-persona-picker__option${currentPersonaId === p.id ? ' suno-persona-picker__option--selected' : ''}${isOwn ? ' suno-persona-picker__option--own' : ''}`}
								onClick={() => {
									onApply(p.id)
									setOpen(false)
								}}
							>
								{p.image_url && (
									<img
										src={p.image_url}
										alt=""
										className="suno-persona-picker__option-img"
										loading="lazy"
									/>
								)}
								<div className="suno-persona-picker__option-main">
									<div className="suno-persona-picker__option-title">
										{p.name || 'Без названия'}
										{isOwn && (
											<span className="suno-persona-picker__badge" title="Создан из этого трека">
												из этого трека
											</span>
										)}
										{!isOwn && p.source === 'upload' && (
											<span className="suno-persona-picker__badge" title="Создан из вашего аудио">
												свой голос
											</span>
										)}
									</div>
									<div className="suno-persona-picker__option-desc">
										{p.description || `ID: ${p.external_persona_id.slice(0, 12)}…`}
									</div>
								</div>
								{currentPersonaId === p.id && <span className="suno-persona-picker__check">✓</span>}
							</button>
						)
					})}
		</div>
	)

	// ── Section-режим: строка «Голос ›» + drill-in сабменю (Artlist) ──
	if (asSection) {
		return (
			<div className={`bbar-section bbar-section--nav${dimmed ? ' bbar-section--dim' : ''}`}>
				<button
					ref={rowRef}
					type="button"
					className={`bbar-navrow${open ? ' bbar-navrow--open' : ''}`}
					onClick={() => setOpen((v) => !v)}
					disabled={effDisabled}
					aria-expanded={open}
				>
					<span className="bbar-navrow__icon" aria-hidden="true">
						<Icon id="gen-audio" size={14} />
					</span>
					<span className="bbar-navrow__label">Голос</span>
					<span className="bbar-navrow__value">{blockedReason ? 'Недоступно' : selected ? label : 'Авто'}</span>
					<span className="bbar-navrow__chevron" aria-hidden="true" />
				</button>

				<ToolsSubmenu
					open={open}
					anchorRef={rowRef}
					onClose={() => setOpen(false)}
					width={340}
					className="gm-tools-submenu--persona"
				>
					<div className="gm-tools-submenu__title">Выбор голоса (Персоны)</div>
					<div className="gm-tools-submenu__body">{body}</div>
				</ToolsSubmenu>
			</div>
		)
	}

	return (
		<>
			<button
				type="button"
				className={`bbar-pill suno-persona-btn${effDisabled ? ' bbar-pill--disabled' : ''}${dimmed ? ' bbar-pill--dim' : ''}`}
				onClick={() => setOpen(true)}
				disabled={effDisabled}
				title={blockedReason ?? (selected ? `Голос: ${selected.name || 'Персона'}` : 'Выбрать голос (Персону)')}
			>
				<span className="bbar-pill__icon" aria-hidden="true">
					<Icon id="gen-audio" size={14} />
				</span>
				<span className="bbar-pill__label">{label}</span>
			</button>

			<Sheet
				open={open}
				onClose={() => setOpen(false)}
				variant="center"
				title="Выбор голоса (Персоны)"
				className="suno-persona-picker"
			>
				{body}
			</Sheet>
		</>
	)
}
